import { useLocation } from "react-router-dom";

import { Button } from "@braintrust/braintrust-ui-components";
import { useMediaQuery } from "@braintrust/braintrust-ui-components";
import { CloseIcon } from "@braintrust/braintrust-ui-components/Icons";
import { openMakeOfferMessageModal } from "@js/apps/jobs/apps/bids";
import { getMakeOfferUrl } from "@js/apps/jobs/utils";
import { useGoBackHistory, useNavigate } from "@js/hooks";
import type { EmployerBid, EmployerOffer } from "@js/types/jobs";
import { isNotNullable, normalizePath } from "@js/utils";

import { CONTRACT_HISTORY_TABLE_QUERY_PARAM_PREFIX } from "../contracts-history-table/const";
import { EmployerOfferStatusWithJobTitle } from "../offer-status-with-job-title";

import { EmployerOfferCTAButtons } from "./employer-navigation-buttons";
import { OfferNavigation } from "./helpers";

type EmployerNavigationProps = {
  jobId: number;
  jobTitle: string;
  offerStatus: EnumType<typeof ENUMS.OfferStatus> | null;
  offerId?: number;
  interlocutor: number;
  previewPage?: boolean;
  bid?: EmployerBid;
  pdfFile?: EmployerOffer["pdf_file"];
};

export const EmployerNavigation = ({
  jobId,
  jobTitle,
  offerStatus,
  offerId,
  interlocutor,
  previewPage,
  bid,
  pdfFile,
}: EmployerNavigationProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const goBack = useGoBackHistory();
  const isMobile = useMediaQuery("sm");

  const handleGoBackClick = () => {
    const prevSearchParams = new URLSearchParams(location.state?.prevSearch);
    const prevSearchParamsString = prevSearchParams.toString();
    const search = prevSearchParamsString ? `?${prevSearchParamsString}` : "";
    const prevPath = location.state?.prevPath;
    const isPrevPathOfferEdit =
      !!prevPath && normalizePath(prevPath).endsWith("/edit");
    const nonEditableOfferStatuses: Array<EnumType<typeof ENUMS.OfferStatus>> =
      [ENUMS.OfferStatus.CANCELLED];

    const isOfferEditDisabled =
      offerStatus && nonEditableOfferStatuses.includes(offerStatus);

    if (isPrevPathOfferEdit && isOfferEditDisabled) {
      navigate(`/jobs/${jobId}/proposals/${search}`);
      return;
    }

    if (bid && !offerStatus) {
      const makeOfferUrl = getMakeOfferUrl({ jobId, bidId: bid.id });

      openMakeOfferMessageModal({
        bid,
        nextUrl: makeOfferUrl,
      });
      // to prevent redirection to a link with open drawer,
      // which is fully fetched after opening of the modal and takes focus from it
      navigate(`/jobs/${jobId}/proposals/${search}`);
      return;
    }

    if (location.search.includes(CONTRACT_HISTORY_TABLE_QUERY_PARAM_PREFIX)) {
      navigate(`/jobs/${jobId}/proposals/${search}`);
      return;
    }

    goBack(`/jobs/${jobId}/proposals/${search}`);
  };

  const showElements = isMobile ? isNotNullable(offerStatus) : true;

  return (
    <OfferNavigation
      leftSocket={
        showElements ? (
          <Button
            variant="transparent"
            shape="squared"
            onClick={handleGoBackClick}
            startIcon={<CloseIcon />}
          >
            Go back
          </Button>
        ) : undefined
      }
      centerSocket={
        <EmployerOfferStatusWithJobTitle
          jobId={jobId}
          jobTitle={jobTitle}
          offerStatus={offerStatus}
          showTitle={showElements}
        />
      }
      rightSocket={
        <EmployerOfferCTAButtons
          pdfFile={pdfFile}
          jobId={jobId}
          offerId={offerId}
          offerStatus={offerStatus}
          interlocutor={interlocutor}
          previewPage={previewPage}
        />
      }
    />
  );
};
